import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  GAME_SEAT_KEY,
  GUEST_COLOR,
  gameDocName,
  gameKindLabel,
  type GameMeta,
  type GuestSeatResponse,
} from '@awwyeah/shared';
import { useYDoc, useYMap } from '../../sync';
import { GameBoard } from './GameBoardPage';
import { apiClaimGuestSeat, apiReadGuestSeat } from './guestApi';
import { GuestPlayerProvider, type KnownMember } from './guestPlayer';

/**
 * `/play/:token` — the whole guest experience (WS-23), and the only page in the app that
 * renders outside both the family chrome and `RequireAuth` (with its friend-of-the-family
 * siblings `/play/lounge` and `/play/g/:gameId`).
 *
 * Three states, and the transitions between them are the feature:
 *
 *   `open`   somebody sent you a link and the seat is free → name field (skippable) → claim
 *   `mine`   you hold the seat → the live board, same components the household plays on
 *   `taken`  somebody else got there first, or the link was cancelled → one plain sentence
 *
 * A missing/garbage token renders as `taken` too. That is deliberate: the server already
 * refuses to distinguish them, and the page must not reintroduce the difference.
 *
 * There is no sign-up prompt anywhere on this page and no notification of any kind. A guest
 * keeps the tab open and plays; that's the whole contract (`memory/no-demand-mechanics.md`).
 * A returning friend of the family (`seat.knownAs`) is greeted by name, and claiming keeps
 * them the same friend — their lounge gains this game.
 */
export function GuestPlayPage(): JSX.Element {
  const { token } = useParams<{ token: string }>();
  const [seat, setSeat] = useState<GuestSeatResponse | null | 'loading'>('loading');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setSeat(null);
      return;
    }
    void apiReadGuestSeat(token)
      .then((result) => {
        if (cancelled) return;
        setSeat(result);
        if (result?.knownAs) setName(result.knownAs);
      })
      .catch(() => {
        if (!cancelled) setSeat(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const claim = useCallback(
    async (chosen?: string) => {
      if (!token) return;
      setBusy(true);
      try {
        setSeat(await apiClaimGuestSeat(token, chosen));
      } catch {
        setSeat(null);
      } finally {
        setBusy(false);
      }
    },
    [token],
  );

  if (seat === 'loading') {
    return <GuestShell>Opening the game…</GuestShell>;
  }

  // `null` (no such link) and `taken` are one screen on purpose — see the docblock.
  if (!seat || seat.state === 'taken') {
    return (
      <GuestShell testId="guest-seat-taken">
        <p className="text-base text-fg">That seat&apos;s taken.</p>
        <p className="text-sm text-fg-3">Ask for a fresh link if you were expecting to play.</p>
        <Link to="/play/lounge" className="text-sm font-semibold text-fg-2 underline">
          Already a friend of the family? Your games are in the lounge.
        </Link>
      </GuestShell>
    );
  }

  if (seat.state === 'open') {
    return (
      <GuestShell testId="guest-seat-open">
        {seat.knownAs && (
          <p className="text-sm font-semibold text-fg-3" data-testid="guest-welcome-back">
            Welcome back, {seat.knownAs} 👋
          </p>
        )}
        <p className="text-lg font-semibold text-fg">
          {seat.invitedBy ? `${seat.invitedBy} asked you to play` : 'You’ve been asked to play'}
        </p>
        {seat.game && (
          <p className="flex items-center gap-2 text-sm text-fg-2" data-testid="guest-seat-kind">
            <span
              className="h-3 w-3 shrink-0 rounded-full"
              style={{ backgroundColor: seat.invitedByColor ?? GUEST_COLOR }}
              aria-hidden="true"
            />
            {gameKindLabel(seat.game.kind)} · {seat.game.title}
          </p>
        )}
        <label className="flex flex-col gap-1 text-sm text-fg-2" htmlFor="guest-name">
          What should we call you?
          <input
            id="guest-name"
            data-testid="guest-name-input"
            value={name}
            maxLength={40}
            onChange={(event) => setName(event.target.value)}
            placeholder="Guest"
            className="h-12 rounded-md border border-line bg-surface px-3 text-base text-fg"
          />
        </label>
        <button
          type="button"
          data-testid="guest-claim-button"
          disabled={busy}
          onClick={() => void claim(name.trim() || undefined)}
          className="h-12 rounded-md bg-inverse text-base font-semibold text-inverse-fg disabled:opacity-50"
        >
          Play
        </button>
        <button
          type="button"
          data-testid="guest-skip-button"
          disabled={busy}
          onClick={() => void claim()}
          className="h-11 rounded-md border border-line text-sm font-semibold text-fg-2 disabled:opacity-50"
        >
          Skip, just play
        </button>
      </GuestShell>
    );
  }

  return <ClaimedGuestGame seat={seat} />;
}

/** The members a seat response lets the guest's board name (the one across the table). */
export function knownMembersFromSeat(seat: GuestSeatResponse): KnownMember[] {
  if (!seat.invitedById || !seat.invitedBy) return [];
  return [
    { id: seat.invitedById, name: seat.invitedBy, color: seat.invitedByColor ?? GUEST_COLOR },
  ];
}

/** The seat is ours: announce who we are to the match doc, then hand over to the board. */
export function ClaimedGuestGame({ seat }: { seat: GuestSeatResponse }): JSX.Element {
  const meta: GameMeta | null = seat.game ?? null;
  const guestId = seat.guestId;
  const guestName = seat.guestName;

  if (!meta || !guestId || !guestName) {
    return <GuestShell testId="guest-seat-unavailable">This game isn&apos;t here.</GuestShell>;
  }

  return (
    <GuestPlayerProvider
      guestId={guestId}
      guestName={guestName}
      knownMembers={knownMembersFromSeat(seat)}
    >
      <GuestBoard meta={meta} guestId={guestId} guestName={guestName} />
    </GuestPlayerProvider>
  );
}

function GuestBoard({
  meta,
  guestId,
  guestName,
}: {
  meta: GameMeta;
  guestId: string;
  guestName: string;
}): JSX.Element {
  const doc = useYDoc(gameDocName(meta.id));
  const { map } = useYMap<unknown>(doc, GAME_SEAT_KEY);

  // Announce the chosen name in the one doc a guest may write. Members mirror it up into the
  // registry so the games index and the timeline can name us too — see `guestSeat.ts`.
  useEffect(() => {
    if (!map) return;
    if (map.get('guestId') === guestId && map.get('guestName') === guestName) return;
    doc?.transact(() => {
      map.set('guestId', guestId);
      map.set('guestName', guestName);
      map.set('joinedAt', new Date().toISOString());
    });
  }, [doc, map, guestId, guestName]);

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3 p-4">
      <div className="flex items-center justify-between gap-2 text-xs">
        <span
          className="rounded-full border border-line px-2.5 py-1 font-semibold text-fg-3"
          data-testid="guest-friend-badge"
        >
          🎟️ friend of the family
        </span>
        <Link
          to="/play/lounge"
          className="font-semibold text-fg-2 underline"
          data-testid="guest-lounge-link"
        >
          Your games →
        </Link>
      </div>
      <GameBoard gameId={meta.id} meta={meta} />
    </div>
  );
}

export function GuestShell({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId?: string;
}): JSX.Element {
  return (
    <main
      className="mx-auto flex min-h-screen w-full max-w-sm flex-col justify-center gap-3 p-6"
      {...(testId ? { 'data-testid': testId } : {})}
    >
      {children}
    </main>
  );
}
